import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { uploadImage, validateImageFile } from '../../lib/storage'

function SubmitEvent() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState('')

  // Scroll vers le haut au chargement de la page
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  // Données du formulaire
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    date_start: '',
    date_end: '',
    date_show: '',
    price: 'Gratuite',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    compte_insta: '',
    alt_text: '',
  })

  const [categories, setCategories] = useState([])
  const [imageFile, setImageFile] = useState(null)
  const [imagePreview, setImagePreview] = useState(null)

  const typeCategories = [
    { value: 'votive', label: 'Fête Votive' },
    { value: 'petanque', label: 'Pétanque' },
    { value: 'foraine', label: 'Fête Foraine' }
  ]

  const monthCategories = [
    { value: 'juin', label: 'Juin' },
    { value: 'juillet', label: 'Juillet' },
    { value: 'aout', label: 'Août' },
    { value: 'septembre', label: 'Septembre' },
    { value: 'hivernal', label: 'Hivernal (Oct-Mai)' }
  ]

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  const handleCategoryToggle = (categoryValue) => {
    if (categories.includes(categoryValue)) {
      setCategories(categories.filter(c => c !== categoryValue))
    } else {
      setCategories([...categories, categoryValue])
    }
  }

  const handleMonthSelect = (monthValue) => {
    // Retirer tous les mois existants et ajouter le nouveau
    const monthValues = monthCategories.map(m => m.value)
    const otherCategories = categories.filter(c => !monthValues.includes(c))
    setCategories([...otherCategories, monthValue])
  }

  const handleImageChange = (e) => {
    const file = e.target.files[0]
    if (!file) return

    try {
      validateImageFile(file)
      setImageFile(file)
      setImagePreview(URL.createObjectURL(file))
      setError('')
    } catch (err) {
      setError(err.message)
      setImageFile(null)
      setImagePreview(null)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      // Validation
      if (!imageFile) {
        throw new Error('Veuillez ajouter une affiche de votre événement')
      }

      if (categories.length === 0) {
        throw new Error('Veuillez sélectionner au moins une catégorie')
      }

      if (!formData.date_start || !formData.date_end) {
        throw new Error('Veuillez renseigner les dates de début et de fin')
      }

      // Upload de l'image
      const { url: imageUrl, path: imagePath } = await uploadImage(imageFile, 'submissions')

      // Créer la soumission
      const { error: submitError } = await supabase
        .from('event_submissions')
        .insert([{
          title: formData.title,
          description: formData.description,
          date_start: formData.date_start,
          date_end: formData.date_end,
          date_show: formData.date_show,
          price: formData.price,
          categories: categories,
          compte_insta: formData.compte_insta,
          image_url: imageUrl,
          image_path: imagePath,
          alt_text: formData.alt_text || formData.title,
          contact_name: formData.contact_name,
          contact_email: formData.contact_email,
          contact_phone: formData.contact_phone,
          status: 'pending'
        }])

      if (submitError) throw submitError

      // Succès !
      setSuccess(true)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } catch (err) {
      console.error('Erreur lors de la soumission:', err)
      setError(err.message || 'Une erreur est survenue lors de la soumission')
    } finally {
      setLoading(false)
    }
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gray-100 py-12 px-4">
        <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="mb-6">
            <svg className="mx-auto h-16 w-16 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-3xl font-bold text-gray-800 mb-4">Merci pour votre soumission !</h2>
          <p className="text-gray-600 mb-6">
            Votre événement a été envoyé avec succès. Il sera examiné par notre équipe et publié prochainement sur le site.
          </p>
          <p className="text-sm text-gray-500 mb-8">
            Vous recevrez une confirmation par email à <strong>{formData.contact_email}</strong>
          </p>
          <div className="flex gap-4 justify-center">
            <button
              onClick={() => navigate('/')}
              className="bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-md font-medium"
            >
              Retour à l'accueil
            </button>
            <button
              onClick={() => {
                setSuccess(false)
                setFormData({
                  title: '',
                  description: '',
                  date_start: '',
                  date_end: '',
                  date_show: '',
                  price: 'Gratuite',
                  contact_name: '',
                  contact_email: '',
                  contact_phone: '',
                  compte_insta: '',
                  alt_text: '',
                })
                setCategories([])
                setImageFile(null)
                setImagePreview(null)
              }}
              className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-3 rounded-md font-medium"
            >
              Soumettre un autre événement
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-100 py-12 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-4">Proposer une fête votive</h1>
          <p className="text-gray-600">
            Organisez une fête dans votre village ? Partagez-la avec la communauté !
          </p>
        </div>

        {/* Formulaire */}
        <div className="bg-white rounded-lg shadow-lg p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Message d'erreur */}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                {error}
              </div>
            )}

            {/* Titre */}
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
                Titre de l'événement *
              </label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Ex: Fête Votive de Saint-Jean"
              />
            </div>

            {/* Description */}
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                Description *
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                required
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Décrivez votre événement (programme, animations, restauration...)"
              />
            </div>

            {/* Dates */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="date_start" className="block text-sm font-medium text-gray-700 mb-2">
                  Date de début *
                </label>
                <input
                  type="datetime-local"
                  id="date_start"
                  name="date_start"
                  value={formData.date_start}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>
              <div>
                <label htmlFor="date_end" className="block text-sm font-medium text-gray-700 mb-2">
                  Date de fin *
                </label>
                <input
                  type="datetime-local"
                  id="date_end"
                  name="date_end"
                  value={formData.date_end}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Date à afficher */}
            <div>
              <label htmlFor="date_show" className="block text-sm font-medium text-gray-700 mb-2">
                Date à afficher sur le site *
              </label>
              <input
                type="text"
                id="date_show"
                name="date_show"
                value={formData.date_show}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Ex: Samedi 15 Juillet 2025 | Bal dès 21h"
              />
            </div>

            {/* Prix */}
            <div>
              <label htmlFor="price" className="block text-sm font-medium text-gray-700 mb-2">
                Prix d'entrée
              </label>
              <input
                type="text"
                id="price"
                name="price"
                value={formData.price}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Gratuite"
              />
            </div>

            {/* Types d'événement */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Types d'événement * (plusieurs choix possibles)
              </label>
              <div className="flex flex-wrap gap-2">
                {typeCategories.map(cat => (
                  <button
                    key={cat.value}
                    type="button"
                    onClick={() => handleCategoryToggle(cat.value)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition ${
                      categories.includes(cat.value)
                        ? 'bg-green-500 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Mois */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mois * (un seul choix)
              </label>
              <div className="flex flex-wrap gap-2">
                {monthCategories.map(cat => (
                  <button
                    key={cat.value}
                    type="button"
                    onClick={() => handleMonthSelect(cat.value)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition ${
                      categories.includes(cat.value)
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Image */}
            <div>
              <label htmlFor="image" className="block text-sm font-medium text-gray-700 mb-2">
                Affiche de l'événement * (JPG, PNG ou WebP, max 5 Mo)
              </label>
              <input
                type="file"
                id="image"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleImageChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
              {imagePreview && (
                <div className="mt-4">
                  <img src={imagePreview} alt="Aperçu" className="max-w-xs rounded border" />
                </div>
              )}
            </div>

            {/* Instagram */}
            <div>
              <label htmlFor="compte_insta" className="block text-sm font-medium text-gray-700 mb-2">
                Compte Instagram (optionnel)
              </label>
              <input
                type="url"
                id="compte_insta"
                name="compte_insta"
                value={formData.compte_insta}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="https://www.instagram.com/votre_compte/"
              />
            </div>

            {/* Informations de contact */}
            <div className="border-t pt-6">
              <h3 className="text-lg font-medium text-gray-800 mb-4">Vos informations</h3>

              <div className="space-y-4">
                <div>
                  <label htmlFor="contact_name" className="block text-sm font-medium text-gray-700 mb-2">
                    Votre nom *
                  </label>
                  <input
                    type="text"
                    id="contact_name"
                    name="contact_name"
                    value={formData.contact_name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="Prénom Nom"
                  />
                </div>

                <div>
                  <label htmlFor="contact_email" className="block text-sm font-medium text-gray-700 mb-2">
                    Votre email *
                  </label>
                  <input
                    type="email"
                    id="contact_email"
                    name="contact_email"
                    value={formData.contact_email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="email@exemple.com"
                  />
                </div>

                <div>
                  <label htmlFor="contact_phone" className="block text-sm font-medium text-gray-700 mb-2">
                    Votre téléphone (optionnel)
                  </label>
                  <input
                    type="tel"
                    id="contact_phone"
                    name="contact_phone"
                    value={formData.contact_phone}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="06 12 34 56 78"
                  />
                </div>
              </div>
            </div>

            {/* Boutons */}
            <div className="flex gap-4 pt-6">
              <button
                type="submit"
                disabled={loading}
                className="flex-1 bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-6 rounded-md transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Envoi en cours...' : 'Soumettre mon événement'}
              </button>
              <button
                type="button"
                onClick={() => navigate('/')}
                className="px-6 py-3 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
              >
                Annuler
              </button>
            </div>
          </form>
        </div>

        {/* Note de bas de page */}
        <div className="mt-6 text-center text-sm text-gray-500">
          <p>Votre événement sera examiné avant publication. Vous recevrez une confirmation par email.</p>
        </div>
      </div>
    </div>
  )
}

export default SubmitEvent
